/**
 * Invento's shared Sonner adapter. It deliberately owns the design-system
 * chrome, accessible announcement content, and stable lightweight API so
 * features do not couple themselves to a notification vendor.
 */

import type { ReactNode } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';
import { cn } from '../../lib/cn';
import { ToastDescription } from './toast-description';
import type {
  ToastId,
  ToastOptions,
  ToastPromiseOptions,
  ToastType,
  ToastUpdateOptions,
  ToasterProps,
} from './toast.types';
import './toast.css';

const DEFAULT_DURATION = 5000;

export const Toaster = ({
  visibleToasts = 3,
  gap = 10,
  className,
  theme = 'system',
}: ToasterProps) => (
  <SonnerToaster
    // Notifications are deliberately bottom-end at every width so they stay
    // out of the primary work area without switching interaction patterns.
    position="bottom-right"
    visibleToasts={visibleToasts}
    gap={gap}
    theme={theme}
    closeButton
    // A bounded, expanded stack keeps every visible close/action control
    // reachable instead of leaving collapsed toasts in the tab sequence.
    expand
    richColors={false}
    dir="auto"
    className={cn('invento-toaster', className)}
    containerAriaLabel="Notifications"
    offset={{ bottom: 'max(1rem, env(safe-area-inset-bottom))', right: 'max(1rem, env(safe-area-inset-right))' }}
    mobileOffset={{ bottom: 'max(1rem, env(safe-area-inset-bottom))', right: 'max(1rem, env(safe-area-inset-right))' }}
    icons={{
      success: <CheckCircleIcon className="invento-toast__status-icon" aria-hidden />,
      error: <XCircleIcon className="invento-toast__status-icon" aria-hidden />,
      warning: <ExclamationTriangleIcon className="invento-toast__status-icon" aria-hidden />,
      info: <InformationCircleIcon className="invento-toast__status-icon" aria-hidden />,
      close: <XMarkIcon className="invento-toast__close-icon" aria-hidden />,
    }}
    toastOptions={{
      duration: DEFAULT_DURATION,
      closeButton: true,
      closeButtonAriaLabel: 'Dismiss notification',
      classNames: {
        toast: 'invento-toast',
        title: 'invento-toast__title',
        description: 'invento-toast__description',
        actionButton: 'invento-toast__action',
        cancelButton: 'invento-toast__cancel',
        closeButton: 'invento-toast__close',
      },
    }}
  />
);

Toaster.displayName = 'Toaster';

type ToastInput = ToastOptions | ReactNode;

function normalize(input: ToastInput): ToastOptions {
  return typeof input === 'object' && input !== null && 'title' in input
    ? (input as ToastOptions)
    : { title: input as ReactNode };
}

function toDuration(duration: number | undefined): number | undefined {
  // The legacy adapter used 0 for a persistent toast. Sonner uses Infinity.
  return duration === 0 ? Infinity : duration;
}

function decorate(input: ToastInput): [ReactNode, ToastOptions] {
  const options = normalize(input);
  const announcement = (
    // Sonner owns the notification live region. Adding a nested role here
    // would announce every message twice in several screen-reader/browser
    // combinations.
    <span className="invento-toast__announcement">{options.title}</span>
  );

  return [announcement, options];
}

function descriptionContent(description: ReactNode | undefined): ReactNode | undefined {
  return description === undefined ? undefined : <ToastDescription description={description} />;
}

function toSonnerOptions(options: ToastOptions) {
  return {
    description: descriptionContent(options.description),
    // Keyboard users need time to reach Retry/Dismiss. Action-bearing notices
    // remain until the user acts or explicitly closes them.
    duration: options.action || options.cancel ? Infinity : toDuration(options.duration),
    icon: options.icon,
    action: options.action,
    cancel: options.cancel,
    dismissible: options.dismissible,
    // Do not expose a focusable close control that cannot dismiss.
    closeButton: options.dismissible === false ? false : undefined,
    id: options.id,
    className: options.className,
    style: options.style,
  };
}

function show(type: Exclude<ToastType, 'loading'>, input: ToastInput): ToastId {
  const [title, options] = decorate(input);
  const sonnerOptions = toSonnerOptions(options);

  switch (type) {
    case 'success':
      return sonnerToast.success(title, sonnerOptions);
    case 'error':
      return sonnerToast.error(title, sonnerOptions);
    case 'warning':
      return sonnerToast.warning(title, sonnerOptions);
    case 'info':
      return sonnerToast.info(title, sonnerOptions);
    default:
      return sonnerToast(title, sonnerOptions);
  }
}

export const toast = {
  show: (input: ToastInput): ToastId => show('default', input),
  success: (input: ToastInput): ToastId => show('success', input),
  error: (input: ToastInput): ToastId => show('error', input),
  warning: (input: ToastInput): ToastId => show('warning', input),
  info: (input: ToastInput): ToastId => show('info', input),
  loading: (input: ToastInput): ToastId => {
    const [title, options] = decorate(input);
    return sonnerToast.loading(title, toSonnerOptions(options));
  },
  update: (id: ToastId, options: ToastUpdateOptions): ToastId => {
    const type = options.isLoading ? 'loading' : options.type ?? 'default';
    const input = { ...options, id };
    if (type === 'loading') return toast.loading(input);
    return show(type, input);
  },
  promise: <T,>(promise: Promise<T>, options: ToastPromiseOptions<T>) =>
    sonnerToast.promise(promise, {
      id: options.id,
      loading: options.loading,
      success: options.success,
      error: options.error,
      description: descriptionContent(options.description),
    }),
  dismiss: (id?: ToastId) => sonnerToast.dismiss(id),
};
